import { useCallback, useTransition } from 'react';
import {
  useRecoilState,
  useRecoilState_TRANSITION_SUPPORT_UNSTABLE,
  useSetRecoilState,
} from 'recoil';

import { LeadFormType } from '@/app/_components/modals/enquire-forms/enquiry-forms/enquiry-forms';
import {
  drawerState,
  floatingCTAShowState,
  floatingFormModeState,
  formConfigState,
  formItemState,
  NewCarItem,
  pageFormItemState,
  primaryButtonActionState,
  secondaryFloatingButtonState,
  SpecialItem,
  UsedCarItem,
  whatsappButtonState,
} from '@/states/floating-cta.state';
import { MinimalNewCarItem } from '@/types/new-cars/new-cars.types';

type FormNames = {
  [key in LeadFormType]: string;
};

export const FORM_NAMES = {
  'new-cars': 'NewCars',
  'used-cars': 'UsedCars',
  newsletter: 'Newsletter',
  parts: 'Parts',
  service: 'Service',
  general: 'TestDrive',
  'test-drive': 'TestDrive',
  'trade-in': 'TradeIn',
} as FormNames;

export const useOpenFloatingCTA = () => {
  const setFormConfig = useSetRecoilState(formConfigState);
  const setFormItem = useSetRecoilState(formItemState);
  const setIsDrawerOpen = useSetRecoilState(drawerState);
  const setFloatingFormMode = useSetRecoilState(floatingFormModeState);

  const [_isPending, startTransition] = useTransition();

  return useCallback(
    (
      leadType: LeadFormType,
      formName?: string,
      item?: NewCarItem | UsedCarItem | SpecialItem | MinimalNewCarItem,
    ) => {
      setFloatingFormMode('enquiry');

      setFormConfig({
        formName: formName
          ? formName
          : FORM_NAMES[item?.type === 'special' ? item.leadType : leadType],
        leadType: item?.type === 'special' ? item.leadType : leadType,
      });
      if (item) {
        setFormItem(item);
      }
      startTransition(() => {
        setIsDrawerOpen(true);
      });
    },
    [
      setFormConfig,
      setFormItem,
      setIsDrawerOpen,
      setFloatingFormMode,
      startTransition,
    ],
  );
};

export const useSetFormItem = () => {
  const [_pageFormItem, setPageFormItem] = useRecoilState(pageFormItemState);

  return setPageFormItem;
};

export const useFloatingCTA = () => {
  const [isDrawerOpen, setIsDrawerOpen] =
    useRecoilState_TRANSITION_SUPPORT_UNSTABLE(drawerState);
  const [isCTAShown, setIsCTAShown] =
    useRecoilState_TRANSITION_SUPPORT_UNSTABLE(floatingCTAShowState);
  const [formConfig, setFormConfig] =
    useRecoilState_TRANSITION_SUPPORT_UNSTABLE(formConfigState);
  const [formItem, setFormItem] =
    useRecoilState_TRANSITION_SUPPORT_UNSTABLE(formItemState);
  const [floatingFormMode, setFloatingFormMode] = useRecoilState_TRANSITION_SUPPORT_UNSTABLE(floatingFormModeState);
  const [pageFormItem, setPageFormItem] =
    useRecoilState_TRANSITION_SUPPORT_UNSTABLE(pageFormItemState);
  const [secondaryFloatingButton, setSecondaryFloatingButton] =
    useRecoilState_TRANSITION_SUPPORT_UNSTABLE(secondaryFloatingButtonState);
  const [whatsappButton, setWhatsAppButton] =
    useRecoilState_TRANSITION_SUPPORT_UNSTABLE(whatsappButtonState);
  const [primaryButtonAction, setPrimaryButtonAction] =
    useRecoilState_TRANSITION_SUPPORT_UNSTABLE(primaryButtonActionState);

  const [_isPending, startTransition] = useTransition();

  const openWhatsAppForm = useCallback(() => {
    setFloatingFormMode('whatsapp');

    startTransition(() => {
      setIsDrawerOpen(true);
    });
  }, [setFloatingFormMode, setIsDrawerOpen, startTransition]);

  const openEnquiryForm = useCallback(() => {
    setFloatingFormMode('enquiry');

    startTransition(() => {
      setIsDrawerOpen(true);
    });
  }, [setFloatingFormMode, setIsDrawerOpen, startTransition]);

  const openLeadForm = useCallback(
    (
      leadType: LeadFormType,
      formName?: string,
      item?: NewCarItem | UsedCarItem | SpecialItem | MinimalNewCarItem,
    ) => {
      setFloatingFormMode('enquiry');
      setFormConfig({
        formName: formName
          ? formName
          : FORM_NAMES[item?.type === 'special' ? item.leadType : leadType],
        leadType: item?.type === 'special' ? item.leadType : leadType,
      });
      if (item) {
        setFormItem(item);
      }
      startTransition(() => {
        setIsDrawerOpen(true);
      });
    },
    [
      setFloatingFormMode,
      setFormConfig,
      setFormItem,
      setIsDrawerOpen,
      startTransition,
    ],
  );

  const closeLeadForm = useCallback(() => {
    startTransition(() => {
      setIsDrawerOpen(false);
      setFloatingFormMode('enquiry');
    });
  }, [setIsDrawerOpen, setFloatingFormMode, startTransition]);

  const clearLeadForm = useCallback(() => {
    setFormItem(undefined);
  }, [setFormItem]);

  return {
    isDrawerOpen,
    leadFormName: formConfig.formName,
    openLeadForm,
    openWhatsAppForm,
    openEnquiryForm,
    floatingFormMode,
    closeLeadForm,
    clearLeadForm,
    leadType: formConfig.leadType,
    formConfig,
    formItem: formItem || pageFormItem,
    setFormItem: setPageFormItem,
    isCTAShown,
    setIsCTAShown,
    secondaryFloatingButton,
    setSecondaryFloatingButton,
    primaryButtonAction,
    setPrimaryButtonAction,
    whatsappButton,
    setWhatsAppButton,
  };
};
